import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { setBreadcrumbJsonLd, type BreadcrumbItem } from '../utils/seo';
interface Props {
  darkMode: boolean;
  /** Forum detay gibi sayfalarda zengin JSON-LD başka yerden geliyorsa */
  skipJsonLd?: boolean;
}

const Breadcrumbs: React.FC<Props> = ({ darkMode, skipJsonLd = false }) => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);
  const [forumName, setForumName] = useState<string | null>(null);
  const [categoryLabel, setCategoryLabel] = useState<string | null>(null);

  useEffect(() => {
    const fetchForumName = async () => {
      if (pathnames[0] === 'forum' && pathnames[1]) {
        try {
          const { data, error } = await supabase
            .from('forums')
            .select('name')
            .eq('forum_id', pathnames[1])
            .single();

          if (error) throw error;
          if (data) setForumName(data.name);
        } catch (error) {
          console.error('Error fetching forum name:', error);
        }
      }
    };

    fetchForumName();
  }, [pathnames]);

  useEffect(() => {
    if (pathnames[0] === 'category' && pathnames[1]) {
      setCategoryLabel(decodeURIComponent(pathnames[1]).replace(/-/g, ' '));
    } else {
      setCategoryLabel(null);
    }
  }, [pathnames]);

  const getBreadcrumbName = (path: string, index: number): string => {
    if (index === 1 && pathnames[0] === 'forum' && forumName) return forumName;
    if (index === 1 && pathnames[0] === 'category' && categoryLabel) {
      return categoryLabel.charAt(0).toUpperCase() + categoryLabel.slice(1);
    }

    switch (path) {
      case 'forum':
        return 'Forum Detayı';
      case 'category':
        return index === 0 ? 'Kategoriler' : 'Kategori';
      case 'software':
        return index === 0 ? 'Yazılımlar' : 'Yazılım';
      case 'compare':
        return 'Karşılaştır';
      case 'add':
        return 'Forum öner';
      case 'inactive':
        return 'Forum Mezarlığı';
      case 'rising':
        return 'Yükselenler';
      case 'new-forums':
        return 'Forum topluluğu';
      case 'watchlist':
        return 'Takip listem';
      case 'embed-docs':
        return 'Embed';
      default:
        return decodeURIComponent(path);
    }
  };

  const schemaItems: BreadcrumbItem[] = useMemo(() => {
    const items: BreadcrumbItem[] = [{ name: 'Ana Sayfa', path: '/' }];
    pathnames.forEach((path, index) => {
      const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
      items.push({
        name: getBreadcrumbName(path, index),
        path: routeTo,
      });
    });
    return items;
  }, [pathnames, forumName, categoryLabel]);

  useEffect(() => {
    if (skipJsonLd) return;
    setBreadcrumbJsonLd(schemaItems);
    return () => setBreadcrumbJsonLd([]);
  }, [schemaItems, skipJsonLd]);

  return (
    <nav className="mb-4" aria-label="Breadcrumb">
      <ol className="flex items-center gap-2 text-sm flex-wrap">
        <li>
          <Link
            to="/"
            className={`hover:text-indigo-500 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
          >
            Ana Sayfa
          </Link>
        </li>
        {pathnames.map((path, index) => {
          const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
          const isLast = index === pathnames.length - 1;

          return (
            <React.Fragment key={path}>
              <ChevronRight size={16} className={darkMode ? 'text-gray-600' : 'text-gray-400'} />
              <li>
                {isLast ? (
                  <span className={darkMode ? 'text-white' : 'text-gray-900'} aria-current="page">
                    {getBreadcrumbName(path, index)}
                  </span>
                ) : (
                  <Link
                    to={routeTo}
                    className={`hover:text-indigo-500 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                  >
                    {getBreadcrumbName(path, index)}
                  </Link>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
